import React, { useRef } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import LoaderComponent from "../../../../ReusableComponents/LoaderComponent";

// Register necessary Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const PerformanceChart = ({ labels, header, performanceData, prevMonthData, isLoading, dataSetLabel }) => {
  const chartRef = useRef(null);

  // Calculate MoM % Change (using prevMonthData for 0th index)
  const momPercentage = performanceData.map((value, index, arr) => {
    const prevValue = index === 0 ? prevMonthData : arr[index - 1]; // Use prevMonthData for first index
    if (prevValue === undefined || (prevValue === 0 && value <= 0)) return "0.00%"; // Avoid division by zero
    else if(prevValue === 0 && value > 0) return "100.00%"

    return `${(((value - prevValue) / prevValue) * 100).toFixed(2)}%`;
  });

  // Find the max value in the dataset to adjust Y-axis
  const maxValue = Math.max(...performanceData);
  const yAxisMax = maxValue * 1.2; // Increase by 20% for better visibility

  // Chart Data
  const data = {
    labels,
    datasets: [
      {
        label: dataSetLabel || "Performance",
        data: performanceData,
        backgroundColor: "rgba(0, 123, 255, 0.6)",
        borderRadius: 6,
        barPercentage: 0.5, // Reduce bar width
        categoryPercentage: 0.7,
      },
    ],
  };

  // Custom Plugin to Place MoM % **Exactly** at the Bar Head
  const MoMPlugin = {
    id: "momPercentageLabels",
    afterDatasetsDraw(chart) {
      const { ctx, scales } = chart;
      ctx.save();

      chart.data.datasets[0].data.forEach((value, index) => {
        const x = scales.x.getPixelForValue(index);
        const y = scales.y.getPixelForValue(value); // Get the exact Y position of the bar top
        const momValue = momPercentage[index];

        // Determine text color based on MoM % change
        let textColor = "black"; // Default for 0%
        if (parseFloat(momValue) > 0) textColor = "green";
        else if (parseFloat(momValue) < 0) textColor = "red";

        ctx.font = "12px Arial";
        ctx.fillStyle = textColor;
        ctx.textAlign = "center";

        // **Exactly place MoM % at the bar's max Y position**
        ctx.fillText(momValue, x, y - 5); // Fine-tuned placement
      });

      ctx.restore();
    },
  };

  // Chart Options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: yAxisMax, // Set dynamic max Y-axis value
        ticks: {
          font: { size: 12 }, // Keep Y-axis numbers readable
          color: "#666",
        },
        grid: {
          color: "#ddd",
          drawBorder: false,
        },
      },
      x: {
        ticks: { font: { size: 12 }, color: "#666" },
        grid: { display: false },
      },
    },
    plugins: {
      legend: { display: false }, // Hide legend for simplicity
      title: { display: false },
    },
  };

  return (
    <div>
      
      <div className="chart-card">
      <div style={{display:"flex", width : "100%", justifyContent : "space-between", padding : "0 10px"}}>
        <div style={{fontWeight : 500, fontSize : "14px"}}>{header.title}</div>
        <div>{header.rightContent}</div>
      </div>
        <div style={{ width: "100%", height: "250px", position: "relative", padding: "10px" }}>
          {isLoading ? (
            <LoaderComponent height="5%" width="5%" />
          ) : (
            <Bar ref={chartRef} data={data} options={options} plugins={[MoMPlugin]} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceChart;
